import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subscription } from 'rxjs';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import {
  ModalButton,
  ModalFormField,
  ModalService,
  ModalState,
  ModalText,
  ModalType,
  VersionFileModalData,
  VersionFileSubmitResult
} from '@services/modal/modal.service';
import { ToastService } from '@services/toast/toast.service';
import { LanguageService } from '@services/language/language.service';
import { TableComponent } from '../table/table.component';
import {
  findExistingVersion,
  formatVersionDateInput,
  isExpirationBeforeVersionMonth,
  isValidDateString,
  isValidItemName,
  isValidMonthString,
  isVersionDateInFuture
} from '@shared/utils/version-file.utils';

@Component({
  selector: 'app-modal',
  imports: [CommonModule, ReactiveFormsModule, FormsModule, NgSelectModule, TableComponent],
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  animations: [
    trigger('modalAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-20px)' }))
      ])
    ]),
    trigger('backdropAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0 }))
      ])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class ModalComponent implements OnInit, OnDestroy {
  modals: ModalState[] = [];
  private subscription: Subscription = new Subscription();
  private originalValidators = new WeakMap<AbstractControl, ValidatorFn | null>();
  forms = new Map<string, FormGroup>();
  ModalType = ModalType;
  passwordVisibility = new Map<string, Record<string, boolean>>();
  dragOverModalId: string | null = null;
  translateData: any = {};

  @ViewChild('imageCanvas') imageCanvas: ElementRef<HTMLCanvasElement> | null = null;
  @ViewChild('versionSelectRef') versionSelectRef?: NgSelectComponent;
  @ViewChild('expirationInput') expirationInput?: ElementRef<HTMLInputElement>;
  private acknowledgmentSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private signatureSearchTimer: ReturnType<typeof setTimeout> | null = null;
  // ng-select [addTag]: el texto escrito se convierte en una nueva versión seleccionable.
  addVersionTag = (term: string): string => (term ?? '').toString().trim();
  private ctx: CanvasRenderingContext2D | null = null;
  private image: HTMLImageElement | null = null;
  private cropArea = { x: 0, y: 0, width: 0, height: 0 };
  private isDragging = false;
  private dragStartPos = { x: 0, y: 0 };
  private dragType: 'move' | 'resize' | null = null;
  private resizeHandle: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | null = null;
  private boundDocMouseMove: ((e: MouseEvent) => void) | null = null;
  private boundDocMouseUp: (() => void) | null = null;
  private justFinishedDragging = false;

  private readonly passwordRequirements = {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumber: true,
    requireSpecialChar: true
  };

  constructor(
    private toastService: ToastService,
    private modalService: ModalService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private languageService: LanguageService
  ) { }

  ngOnInit(): void {
    this.subscription.add(
      this.modalService.modals$.subscribe(modals => {
        this.modals = modals;
        this.initFormControls();
        this.cdr.markForCheck();
      })
    );
    this.subscription.add(
      this.languageService.getSharedDictionary('modal').subscribe(dict => {
        this.translateData = dict;
        this.cdr.markForCheck();
      })
    );
    // Listen for field option updates from the service
    this.subscription.add(
      this.modalService.fieldOptionsUpdates$.subscribe(update => {
        this.updateFieldOptions(update.modalId, update.fieldName, update.options);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  closeModal(modal: ModalState, result: any = null): void {
    this.passwordVisibility.delete(modal.id);
    this.modalService.close(modal.id, result);
  }

  getAttributeValue(value: any): any {
    return value !== undefined ? value : null;
  }

  onCheckboxListChange(modal: ModalState, fieldName: string, option: any, event: Event): void {
    const control = this.forms.get(modal.id)?.get(fieldName);
    if (!control) return;
    const current: any[] = control.value || [];
    const checked = (event.target as HTMLInputElement).checked;
    control.setValue(checked ? [...current, option.value] : current.filter((v: any) => v !== option.value));
    if (!checked && option.children?.fieldName) {
      this.forms.get(modal.id)?.get(option.children.fieldName)?.setValue([]);
    }
    this.cdr.markForCheck();
  }

  isCheckboxListOptionSelected(modal: ModalState, fieldName: string, optionValue: any): boolean {
    const values: any[] = this.forms.get(modal.id)?.get(fieldName)?.value || [];
    return values.includes(optionValue);
  }

  /**
   * Updates the options of a form field in an open modal, triggering re-render.
   * Used to dynamically update checkboxlist/select options when language changes.
   */
  updateFieldOptions(modalId: string, fieldName: string, options: { label: string; value: any; children?: { fieldName: string; options: { label: string; value: any }[] } }[]): void {
    const modal = this.modals.find(m => m.id === modalId);
    if (!modal) return;

    // Update the field options in the modal state
    const field = modal.options.formFields?.find(f => f.name === fieldName);
    if (field) {
      field.options = options;
    }

    // Also update child field options if they exist
    if (field?.type === 'checkboxlist') {
      // For checkboxlist with children, update child field options too
      options.forEach(option => {
        if (option.children?.fieldName) {
          const childField = modal.options.formFields?.find(f => f.name === option.children!.fieldName);
          if (childField) {
            childField.options = option.children.options;
          }
        }
      });
    }

    this.cdr.markForCheck();
  }

  onNumberKeyPress(event: KeyboardEvent, field: ModalFormField): void {
    const char = event.key;

    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(char) ||
      (event.ctrlKey && ['a', 'c', 'v', 'x'].includes(char))) {
      return;
    }

    if (/[0-9]/.test(char)) {
      return;
    }

    if (field.allowDecimals === false) {
      event.preventDefault();
      return;
    }

    const inputValue = (event.target as HTMLInputElement).value;

    if (char === '.' && !inputValue.includes('.') && !inputValue.includes(',')) {
      return;
    }
    if (char === ',' && field.allowDecimals && !inputValue.includes(',') && !inputValue.includes('.')) {
      return;
    }

    event.preventDefault();
  }

  submitForm(modal: ModalState): void {
    const form = this.forms.get(modal.id);
    if (!form) return;

    if (modal.options.formFields) {
      modal.options.formFields.forEach(field => {
        const control = form.get(field.name);
        if (control && (modal.options.type === ModalType.FORM ? this.shouldFieldBeVisible(modal, field) : true)) {
          control.markAsTouched();
        }
      });
    }

    if (modal.options.type === ModalType.PASSWORD_CHANGE) {
      form.markAllAsTouched();

      const newPasswordControl = form.get('newPassword');
      const confirmPasswordControl = form.get('confirmPassword');

      if (newPasswordControl && confirmPasswordControl) {
        const newPassword = newPasswordControl.value;
        const confirmPassword = confirmPasswordControl.value;

        if (newPassword !== confirmPassword) {
          confirmPasswordControl.setErrors({ passwordMismatch: true });
        }

        const criteria = this.getPasswordCriteria(newPassword || '');
        const allCriteriaMet = criteria.every(c => c.met);

        if (!allCriteriaMet) {
          newPasswordControl.setErrors({ passwordStrength: true });
        }
      }
    }

    if (form.valid) {
      this.modalService.close(modal.id, form.value);
    } else {
      setTimeout(() => {
        const firstInvalidField = document.querySelector('.modal__form-field .ng-invalid:not(.modal__field--hidden)');
        if (firstInvalidField && firstInvalidField instanceof HTMLElement) {
          firstInvalidField.focus();
        }
      });

      this.cdr.markForCheck();
    }
  }

  onBackdropClick(modal: ModalState, event: MouseEvent): void {
    if (this.justFinishedDragging) return;
    if (modal.options.backdropClose && event.target === event.currentTarget) {
      this.closeModal(modal);
    }
  }

  private initFormControls(): void {
    this.modals.forEach(modal => {
      if ((modal.options.type === ModalType.FORM || modal.options.type === ModalType.PASSWORD_CHANGE)
        && modal.options.formFields && !this.forms.has(modal.id)) {
        const formGroup = this.createFormGroup(modal.options.formFields, modal.options.type);
        this.forms.set(modal.id, formGroup);

        if (modal.options.type === ModalType.FORM) {
          this.setupConditionalFieldListeners(modal.id, modal.options.formFields, formGroup);
        } else if (modal.options.type === ModalType.PASSWORD_CHANGE) {
          this.setupPasswordChangeListeners(modal.id, formGroup);
        }
      }
    });
  }

  private createFormGroup(formFields: ModalFormField[], modalType?: ModalType): FormGroup {
    const group: any = {};

    formFields.forEach(field => {
      const validators: any[] = [];

      if (field.validators && field.validators.length > 0) {
        validators.push(...field.validators);
      }

      if (field.required) {
        validators.push(Validators.required);
      }

      if (field.maxLength !== undefined) {
        validators.push(Validators.maxLength(field.maxLength));
      }

      if (field.type === 'email') {
        validators.push(Validators.email);
      }

      if (field.type === 'number') {
        if (field.min !== undefined) {
          validators.push(Validators.min(field.min));
        }
        if (field.max !== undefined) {
          validators.push(Validators.max(field.max));
        }
      }

      if (modalType === ModalType.PASSWORD_CHANGE && field.name === 'newPassword') {
        validators.push(this.createPasswordValidator());
      }

      const initialValue = (field.type === 'multiselect' || field.type === 'checkboxlist')
        ? (field.value || [])
        : (field.value || '');

      const validatorFn = validators.length > 0
        ? Validators.compose(validators)
        : null;

      group[field.name] = [initialValue, validatorFn];
    });

    const formGroup = this.fb.group(group);

    formFields.forEach(field => {
      if (!field.onChange) return;

      const control = formGroup.get(field.name);
      if (!control) return;

      control.valueChanges.subscribe(value => {
        field.onChange?.(value, formGroup, field);
        this.cdr.markForCheck();
      });
    });

    if (modalType === ModalType.PASSWORD_CHANGE) {
      formGroup.setValidators(this.passwordMatchValidator);
    }

    return formGroup;
  }

  private setupPasswordChangeListeners(modalId: string, formGroup: FormGroup): void {
    const modal = this.modals.find(m => m.id === modalId);
    if (!modal) return;

    const newPasswordControl = formGroup.get('newPassword');
    if (newPasswordControl) {
      newPasswordControl.valueChanges.subscribe(value => {
        this.modalService.update(modalId, {
          data: {
            ...modal.options.data,
            password: value || '',
            showRequirements: value && value.length > 0
          }
        });
        this.cdr.markForCheck();
      });
    }
  }

  private setupConditionalFieldListeners(modalId: string, formFields: ModalFormField[], formGroup: FormGroup): void {
    const conditionalFields = formFields.filter(field => field.showWhen && field.showWhen.length > 0);

    if (conditionalFields.length === 0) return;

    conditionalFields.forEach(field => {
      if (!field.showWhen) return;

      field.showWhen.forEach(condition => {
        const controlField = formGroup.get(condition.field);
        if (!controlField) return;

        this.updateFieldVisibility(modalId, field, formGroup);

        controlField.valueChanges.subscribe(() => {
          this.updateFieldVisibility(modalId, field, formGroup);
          this.cdr.markForCheck();
        });
      });
    });
  }

  private updateFieldVisibility(modalId: string, field: ModalFormField, formGroup: FormGroup): void {
    if (!field.showWhen) return;

    const isVisible = field.showWhen.some(condition => {
      const controlField = formGroup.get(condition.field);
      if (!controlField) return false;

      const controlValue = controlField.value;
      const targetValue = condition.value;
      const operator = condition.operator || 'eq';

      switch (operator) {
        case 'eq':
          return controlValue === targetValue;
        case 'neq':
          return controlValue !== targetValue;
        case 'contains':
          return controlValue?.includes(targetValue);
        case 'in':
          return Array.isArray(targetValue) && targetValue.includes(controlValue);
        default:
          return false;
      }
    });

    const fieldControl = formGroup.get(field.name);
    if (!fieldControl) return;

    if (!this.originalValidators.has(fieldControl)) {
      this.originalValidators.set(fieldControl, fieldControl.validator);
    }

    if (isVisible) {
      fieldControl.setValidators(this.originalValidators.get(fieldControl) || null);
    } else {
      fieldControl.setValidators(null);
      fieldControl.setValue('');
    }

    fieldControl.updateValueAndValidity();

    setTimeout(() => {
      const fieldElement = document.querySelector(`#field_${modalId}_container_${field.name}`);
      if (fieldElement) {
        if (isVisible) {
          fieldElement.classList.remove('modal__field--hidden');
        } else {
          fieldElement.classList.add('modal__field--hidden');
        }
      }
    });
  }

  shouldFieldBeVisible(modal: ModalState, field: ModalFormField): boolean {
    if (!field.showWhen) return true;

    const form = this.forms.get(modal.id);
    if (!form) return true;

    return field.showWhen.some(condition => {
      const controlField = form.get(condition.field);
      if (!controlField) return false;

      const controlValue = controlField.value;
      const targetValue = condition.value;
      const operator = condition.operator || 'eq';

      switch (operator) {
        case 'eq':
          return controlValue === targetValue;
        case 'neq':
          return controlValue !== targetValue;
        case 'contains':
          return controlValue?.includes(targetValue);
        case 'in':
          return Array.isArray(targetValue) && targetValue.includes(controlValue);
        default:
          return false;
      }
    });
  }

  getErrorMessage(modal: ModalState, fieldName: string): string {
    const form = this.forms.get(modal.id);
    if (!form) return '';

    const control = form.get(fieldName);
    if (!control || !control.errors || !control.touched) return '';

    const field = modal.options.formFields?.find(f => f.name === fieldName);
    if (field?.errorMessage) return field.errorMessage;

    if (control.errors['required']) return 'This field is required';
    if (control.errors['email']) return 'Invalid email format';
    if (control.errors['min']) return `Minimum value is ${control.errors['min'].min}`;
    if (control.errors['max']) return `Maximum value is ${control.errors['max'].max}`;
    if (control.errors['maxlength']) return `Maximum length is ${control.errors['maxlength'].requiredLength} characters`;

    if (control.errors['passwordStrength']) {
      return 'Password does not meet security requirements';
    }

    if (control.errors['passwordMismatch']) {
      return 'Passwords do not match';
    }

    if (fieldName === 'confirmPassword' && form.errors?.['passwordMismatch']) {
      return 'Passwords do not match';
    }

    if (field?.type === 'number' && (control.errors['pattern'] || control.errors['NaN'])) {
      return 'Please enter a valid number';
    }

    return 'Invalid value';
  }

  getIconName(type: ModalType | undefined): string {
    switch (type) {
      case ModalType.INFO: return 'info';
      case ModalType.WARNING: return 'warning';
      case ModalType.ERROR: return 'error';
      case ModalType.CONFIRM: return 'help_outline';
      case ModalType.SHARE: return 'share';
      default: return '';
    }
  }

  getIconClass(type: ModalType | undefined): string {
    switch (type) {
      case ModalType.INFO: return 'modal__icon--info';
      case ModalType.WARNING: return 'modal__icon--warning';
      case ModalType.ERROR: return 'modal__icon--error';
      case ModalType.CONFIRM: return 'modal__icon--confirm';
      default: return '';
    }
  }

  onDropzoneDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDropzoneDragEnter(event: DragEvent, modalId: string): void {
    event.preventDefault();
    this.dragOverModalId = modalId;
    this.cdr.markForCheck();
  }

  onDropzoneDragLeave(event: DragEvent, modalId: string): void {
    const target = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as Node | null;
    if (!relatedTarget || !target.contains(relatedTarget)) {
      this.dragOverModalId = null;
      this.cdr.markForCheck();
    }
  }

  onDropFile(event: DragEvent, modal: ModalState): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOverModalId = null;
    const file = event.dataTransfer?.files[0];
    if (!file) return;
    this.processImageFile(file, modal);
  }

  onImageFileSelected(event: Event, modal: ModalState): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.processImageFile(input.files[0], modal);
    }
  }

  private processImageFile(file: File, modal: ModalState): void {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (!e.target?.result) return;

      this.image = new Image();
      this.image.onload = () => {
        setTimeout(() => {
          this.initializeCanvas(modal);
        }, 0);
      };
      this.image.src = e.target.result as string;

      this.modalService.update(modal.id, {
        data: {
          ...modal.options.data,
          imageUrl: e.target.result,
          zoom: 1,
          rotation: 0,
          cropRatio: modal.options.data?.cropRatio || 'free'
        }
      });
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(file);
  }

  private initializeCanvas(modal: ModalState): void {
    if (!this.imageCanvas || !this.image) return;

    const canvas = this.imageCanvas.nativeElement;
    this.ctx = canvas.getContext('2d');

    if (!this.ctx) return;

    const container = canvas.parentElement;
    if (!container) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const scale = Math.min(
      containerWidth / this.image.width,
      containerHeight / this.image.height
    );

    canvas.width = this.image.width * scale;
    canvas.height = this.image.height * scale;

    this.cropArea = { x: 0, y: 0, width: canvas.width, height: canvas.height };

    this.drawCanvas();
    this.setupCanvasEvents(canvas);
  }

  private drawCanvas(): void {
    if (!this.ctx || !this.image || !this.imageCanvas) return;

    const canvas = this.imageCanvas.nativeElement;
    const zoom = this.getActiveModal()?.options.data?.zoom || 1;
    const rotation = this.getActiveModal()?.options.data?.rotation || 0;

    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.ctx.save();

    const scale = Math.min(
      canvas.width / this.image.width,
      canvas.height / this.image.height
    );

    this.ctx.translate(canvas.width / 2, canvas.height / 2);
    this.ctx.rotate((rotation * Math.PI) / 180);
    this.ctx.scale(scale * zoom, scale * zoom);
    this.ctx.translate(-this.image.width / 2, -this.image.height / 2);
    this.ctx.drawImage(this.image, 0, 0);

    this.ctx.restore();

    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';

    this.ctx.beginPath();
    this.ctx.rect(0, 0, canvas.width, canvas.height);
    this.ctx.moveTo(this.cropArea.x, this.cropArea.y);
    this.ctx.lineTo(this.cropArea.x + this.cropArea.width, this.cropArea.y);
    this.ctx.lineTo(this.cropArea.x + this.cropArea.width, this.cropArea.y + this.cropArea.height);
    this.ctx.lineTo(this.cropArea.x, this.cropArea.y + this.cropArea.height);
    this.ctx.closePath();

    this.ctx.fill('evenodd');

    this.ctx.strokeStyle = '#2196f3';
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(this.cropArea.x, this.cropArea.y, this.cropArea.width, this.cropArea.height);

    this.drawResizeHandles();
  }

  private drawResizeHandles(): void {
    if (!this.ctx) return;

    const handleSize = 10;
    this.ctx.fillStyle = '#2196f3';

    this.ctx.fillRect(
      this.cropArea.x - handleSize / 2,
      this.cropArea.y - handleSize / 2,
      handleSize,
      handleSize
    );

    this.ctx.fillRect(
      this.cropArea.x + this.cropArea.width - handleSize / 2,
      this.cropArea.y - handleSize / 2,
      handleSize,
      handleSize
    );

    this.ctx.fillRect(
      this.cropArea.x - handleSize / 2,
      this.cropArea.y + this.cropArea.height - handleSize / 2,
      handleSize,
      handleSize
    );

    this.ctx.fillRect(
      this.cropArea.x + this.cropArea.width - handleSize / 2,
      this.cropArea.y + this.cropArea.height - handleSize / 2,
      handleSize,
      handleSize
    );
  }

  private setupCanvasEvents(canvas: HTMLCanvasElement): void {
    canvas.removeEventListener('mousedown', this.onCanvasMouseDown.bind(this));
    canvas.removeEventListener('mousemove', this.onCanvasMouseMove.bind(this));
    canvas.removeEventListener('mouseup', this.onCanvasMouseUp.bind(this));

    canvas.addEventListener('mousedown', this.onCanvasMouseDown.bind(this));
    canvas.addEventListener('mousemove', this.onCanvasMouseMove.bind(this));
    canvas.addEventListener('mouseup', this.onCanvasMouseUp.bind(this));

    canvas.addEventListener('touchstart', (e) => this.onCanvasTouchStart(e));
    canvas.addEventListener('touchmove', (e) => this.onCanvasTouchMove(e));
    canvas.addEventListener('touchend', () => this.onCanvasMouseUp());
  }

  private onCanvasTouchStart(e: TouchEvent): void {
    if (e.touches.length !== 1 || !this.imageCanvas) return;

    const touch = e.touches[0];
    const rect = this.imageCanvas.nativeElement.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    const mouseEvent = {
      clientX: touch.clientX,
      clientY: touch.clientY,
      offsetX: x,
      offsetY: y,
      preventDefault: () => e.preventDefault()
    } as MouseEvent;

    this.onCanvasMouseDown(mouseEvent);
    e.preventDefault();
  }

  private onCanvasTouchMove(e: TouchEvent): void {
    if (e.touches.length !== 1 || !this.isDragging || !this.imageCanvas) return;

    const touch = e.touches[0];
    const rect = this.imageCanvas.nativeElement.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    const mouseEvent = {
      clientX: touch.clientX,
      clientY: touch.clientY,
      offsetX: x,
      offsetY: y,
      preventDefault: () => e.preventDefault()
    } as MouseEvent;

    this.onCanvasMouseMove(mouseEvent);
    e.preventDefault();
  }

  private onCanvasMouseDown(e: MouseEvent): void {
    const { offsetX, offsetY } = e;
    const handleSize = 10;

    if (Math.abs(offsetX - this.cropArea.x) <= handleSize &&
      Math.abs(offsetY - this.cropArea.y) <= handleSize) {
      this.dragType = 'resize';
      this.resizeHandle = 'topLeft';
    } else if (Math.abs(offsetX - (this.cropArea.x + this.cropArea.width)) <= handleSize &&
      Math.abs(offsetY - this.cropArea.y) <= handleSize) {
      this.dragType = 'resize';
      this.resizeHandle = 'topRight';
    } else if (Math.abs(offsetX - this.cropArea.x) <= handleSize &&
      Math.abs(offsetY - (this.cropArea.y + this.cropArea.height)) <= handleSize) {
      this.dragType = 'resize';
      this.resizeHandle = 'bottomLeft';
    } else if (Math.abs(offsetX - (this.cropArea.x + this.cropArea.width)) <= handleSize &&
      Math.abs(offsetY - (this.cropArea.y + this.cropArea.height)) <= handleSize) {
      this.dragType = 'resize';
      this.resizeHandle = 'bottomRight';
    } else if (offsetX >= this.cropArea.x && offsetX <= this.cropArea.x + this.cropArea.width &&
      offsetY >= this.cropArea.y && offsetY <= this.cropArea.y + this.cropArea.height) {
      this.dragType = 'move';
    } else {
      return;
    }

    this.isDragging = true;
    this.dragStartPos = { x: offsetX, y: offsetY };

    this.boundDocMouseMove = (ev: MouseEvent) => {
      if (!this.imageCanvas) return;
      const rect = this.imageCanvas.nativeElement.getBoundingClientRect();
      this.onCanvasMouseMove({
        offsetX: ev.clientX - rect.left,
        offsetY: ev.clientY - rect.top,
        preventDefault: () => ev.preventDefault()
      } as MouseEvent);
    };
    this.boundDocMouseUp = () => this.onCanvasMouseUp();
    document.addEventListener('mousemove', this.boundDocMouseMove);
    document.addEventListener('mouseup', this.boundDocMouseUp);

    e.preventDefault();
  }

  private onCanvasMouseMove(e: MouseEvent): void {
    if (!this.imageCanvas) return;

    const canvas = this.imageCanvas.nativeElement;
    const { offsetX, offsetY } = e;
    const handleSize = 10;

    canvas.classList.remove('move', 'resize-nw', 'resize-ne', 'resize-sw', 'resize-se');

    if (Math.abs(offsetX - this.cropArea.x) <= handleSize &&
      Math.abs(offsetY - this.cropArea.y) <= handleSize) {
      canvas.classList.add('resize-nw');
    } else if (Math.abs(offsetX - (this.cropArea.x + this.cropArea.width)) <= handleSize &&
      Math.abs(offsetY - this.cropArea.y) <= handleSize) {
      canvas.classList.add('resize-ne');
    } else if (Math.abs(offsetX - this.cropArea.x) <= handleSize &&
      Math.abs(offsetY - (this.cropArea.y + this.cropArea.height)) <= handleSize) {
      canvas.classList.add('resize-sw');
    } else if (Math.abs(offsetX - (this.cropArea.x + this.cropArea.width)) <= handleSize &&
      Math.abs(offsetY - (this.cropArea.y + this.cropArea.height)) <= handleSize) {
      canvas.classList.add('resize-se');
    } else if (offsetX >= this.cropArea.x && offsetX <= this.cropArea.x + this.cropArea.width &&
      offsetY >= this.cropArea.y && offsetY <= this.cropArea.y + this.cropArea.height) {
      canvas.classList.add('move');
    }

    if (!this.isDragging) return;

    const dx = offsetX - this.dragStartPos.x;
    const dy = offsetY - this.dragStartPos.y;

    const activeModal = this.getActiveModal();
    const cropRatio = activeModal?.options.data?.cropRatio || 'free';

    if (this.dragType === 'move') {
      let newX = this.cropArea.x + dx;
      let newY = this.cropArea.y + dy;

      newX = Math.max(0, Math.min(newX, canvas.width - this.cropArea.width));
      newY = Math.max(0, Math.min(newY, canvas.height - this.cropArea.height));

      this.cropArea.x = newX;
      this.cropArea.y = newY;
    } else if (this.dragType === 'resize') {
      let newWidth = this.cropArea.width;
      let newHeight = this.cropArea.height;
      let newX = this.cropArea.x;
      let newY = this.cropArea.y;

      switch (this.resizeHandle) {
        case 'topLeft':
          newX = this.cropArea.x + dx;
          newY = this.cropArea.y + dy;
          newWidth = this.cropArea.width - dx;
          newHeight = this.cropArea.height - dy;
          break;
        case 'topRight':
          newY = this.cropArea.y + dy;
          newWidth = this.cropArea.width + dx;
          newHeight = this.cropArea.height - dy;
          break;
        case 'bottomLeft':
          newX = this.cropArea.x + dx;
          newWidth = this.cropArea.width - dx;
          newHeight = this.cropArea.height + dy;
          break;
        case 'bottomRight':
          newWidth = this.cropArea.width + dx;
          newHeight = this.cropArea.height + dy;
          break;
      }

      if (cropRatio !== 'free') {
        let ratio = 1;

        if (cropRatio === '4:3') ratio = 4 / 3;
        else if (cropRatio === '16:9') ratio = 16 / 9;

        if (Math.abs(dx) > Math.abs(dy)) {
          newHeight = newWidth / ratio;
        } else {
          newWidth = newHeight * ratio;
        }
      }

      const minSize = 50;
      if (newX < 0) { newWidth += newX; newX = 0; }
      if (newX + newWidth > canvas.width) { newWidth = canvas.width - newX; }
      if (newY < 0) { newHeight += newY; newY = 0; }
      if (newY + newHeight > canvas.height) { newHeight = canvas.height - newY; }
      if (newWidth >= minSize && newHeight >= minSize) {
        this.cropArea.x = newX;
        this.cropArea.y = newY;
        this.cropArea.width = newWidth;
        this.cropArea.height = newHeight;
      }
    }

    this.dragStartPos = { x: offsetX, y: offsetY };
    this.drawCanvas();

    e.preventDefault();
  }

  private onCanvasMouseUp(): void {
    if (this.isDragging) {
      this.justFinishedDragging = true;
      setTimeout(() => { this.justFinishedDragging = false; }, 0);
    }
    this.isDragging = false;
    this.dragType = null;
    this.resizeHandle = null;
    if (this.boundDocMouseMove) {
      document.removeEventListener('mousemove', this.boundDocMouseMove);
      this.boundDocMouseMove = null;
    }
    if (this.boundDocMouseUp) {
      document.removeEventListener('mouseup', this.boundDocMouseUp);
      this.boundDocMouseUp = null;
    }
  }

  private getActiveModal(): ModalState | undefined {
    return this.modals.find(modal => modal.options.type === ModalType.PICTURE_UPLOAD);
  }

  setCropRatio(ratio: 'free' | 'square' | '4:3' | '16:9', modal: ModalState): void {
    const currentRatio = modal.options.data?.cropRatio || 'free';

    if (ratio === currentRatio) return;

    this.modalService.update(modal.id, {
      data: {
        ...modal.options.data,
        cropRatio: ratio
      }
    });

    if (ratio !== 'free' && this.imageCanvas) {
      const ratioValue = ratio === 'square' ? 1 : ratio === '4:3' ? 4 / 3 : 16 / 9;
      const centerX = this.cropArea.x + this.cropArea.width / 2;
      const centerY = this.cropArea.y + this.cropArea.height / 2;

      const newWidth = this.cropArea.width;
      const newHeight = newWidth / ratioValue;

      this.cropArea = {
        x: centerX - newWidth / 2,
        y: centerY - newHeight / 2,
        width: newWidth,
        height: newHeight
      };

      const canvas = this.imageCanvas.nativeElement;
      if (this.cropArea.x < 0) this.cropArea.x = 0;
      if (this.cropArea.y < 0) this.cropArea.y = 0;
      if (this.cropArea.x + this.cropArea.width > canvas.width) {
        this.cropArea.width = canvas.width - this.cropArea.x;
        this.cropArea.height = this.cropArea.width / ratioValue;
      }
      if (this.cropArea.y + this.cropArea.height > canvas.height) {
        this.cropArea.height = canvas.height - this.cropArea.y;
        this.cropArea.width = this.cropArea.height * ratioValue;
      }

      this.drawCanvas();
    }

    this.cdr.markForCheck();
  }

  rotateImage(modal: ModalState, degrees: number): void {
    const currentRotation = modal.options.data?.rotation || 0;
    const newRotation = (currentRotation + degrees) % 360;

    this.modalService.update(modal.id, {
      data: {
        ...modal.options.data,
        rotation: newRotation
      }
    });

    this.drawCanvas();
    this.cdr.markForCheck();
  }

  adjustZoom(modal: ModalState, zoomFactor: number): void {
    const currentZoom = modal.options.data?.zoom || 1;
    const newZoom = Math.max(0.1, Math.min(3, currentZoom * zoomFactor));

    this.modalService.update(modal.id, {
      data: {
        ...modal.options.data,
        zoom: newZoom
      }
    });

    this.drawCanvas();
    this.cdr.markForCheck();
  }

  getCroppedImage(): string {
    if (!this.ctx || !this.image || !this.imageCanvas) return '';

    const canvas = this.imageCanvas.nativeElement;
    const zoom = this.getActiveModal()?.options.data?.zoom || 1;
    const rotation = this.getActiveModal()?.options.data?.rotation || 0;

    const displayScale = Math.min(
      canvas.width / this.image.width,
      canvas.height / this.image.height
    );

    const cropX = Math.round(this.cropArea.x / displayScale);
    const cropY = Math.round(this.cropArea.y / displayScale);
    const outputWidth = Math.round(this.cropArea.width / displayScale);
    const outputHeight = Math.round(this.cropArea.height / displayScale);

    const intermediateCanvas = document.createElement('canvas');
    intermediateCanvas.width = this.image.width;
    intermediateCanvas.height = this.image.height;
    const intermediateCtx = intermediateCanvas.getContext('2d');

    if (!intermediateCtx) return '';

    intermediateCtx.clearRect(0, 0, this.image.width, this.image.height);
    intermediateCtx.save();
    intermediateCtx.translate(this.image.width / 2, this.image.height / 2);
    intermediateCtx.rotate((rotation * Math.PI) / 180);
    intermediateCtx.scale(zoom, zoom);
    intermediateCtx.translate(-this.image.width / 2, -this.image.height / 2);
    intermediateCtx.drawImage(this.image, 0, 0);
    intermediateCtx.restore();

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = outputWidth;
    tempCanvas.height = outputHeight;
    const tempCtx = tempCanvas.getContext('2d');

    if (!tempCtx) return '';

    tempCtx.drawImage(
      intermediateCanvas,
      cropX, cropY,
      outputWidth, outputHeight,
      0, 0,
      outputWidth, outputHeight
    );

    const maxFileSize = this.getActiveModal()?.options.data?.maxFileSize || 5242880;
    const pngData = tempCanvas.toDataURL('image/png');
    const pngBytes = Math.round((pngData.length * 3) / 4);

    if (pngBytes <= maxFileSize) {
      return pngData;
    }

    let quality = 0.9;
    let result = tempCanvas.toDataURL('image/jpeg', quality);
    while (Math.round((result.length * 3) / 4) > maxFileSize && quality > 0.1) {
      quality = Math.round((quality - 0.1) * 10) / 10;
      result = tempCanvas.toDataURL('image/jpeg', quality);
    }
    return result;
  }

  saveCroppedImage(modal: ModalState): void {
    if (!this.imageCanvas) return;

    const croppedImageUrl = this.getCroppedImage();
    this.closeModal(modal, {
      imageUrl: croppedImageUrl,
      cropArea: { ...this.cropArea },
      zoom: modal.options.data?.zoom || 1,
      rotation: modal.options.data?.rotation || 0
    });
  }

  resetCrop(modal: ModalState): void {
    if (!this.image || !this.imageCanvas) return;

    const canvas = this.imageCanvas.nativeElement;

    this.cropArea = { x: 0, y: 0, width: canvas.width, height: canvas.height };

    this.modalService.update(modal.id, {
      data: {
        ...modal.options.data,
        zoom: 1,
        rotation: 0
      }
    });

    this.drawCanvas();
    this.cdr.markForCheck();
  }

  onButtonClick(modal: ModalState, button: any): void {
    if (button.action) {
      button.action();
    }

    if (button.close) {
      if (modal.options.type === ModalType.FORM && button.value === true) {
        this.submitForm(modal);
      } else if (modal.options.type === ModalType.PASSWORD_CHANGE && button.value === true) {
        this.submitPasswordChangeForm(modal);
      } else if (modal.options.type === ModalType.PICTURE_UPLOAD && button.value === true) {
        this.saveCroppedImage(modal);
      } else if (
        (modal.options.type === ModalType.CREATE_VERSION || modal.options.type === ModalType.EDIT_VERSION) &&
        button.value === 'submit'
      ) {
        this.submitVersionFile(modal);
      } else {
        this.closeModal(modal, button.value);
      }
    }
  }

  submitPasswordChangeForm(modal: ModalState): void {
    const form = this.forms.get(modal.id);
    if (!form) return;

    form.markAllAsTouched();

    const newPasswordControl = form.get('newPassword');
    const confirmPasswordControl = form.get('confirmPassword');

    if (newPasswordControl && confirmPasswordControl) {
      const newPassword = newPasswordControl.value;
      const confirmPassword = confirmPasswordControl.value;

      if (newPassword !== confirmPassword) {
        confirmPasswordControl.setErrors({ passwordMismatch: true });
      }

      const criteria = this.getPasswordCriteria(newPassword || '');
      const allCriteriaMet = criteria.every(c => c.met);

      if (!allCriteriaMet) {
        newPasswordControl.setErrors({ passwordStrength: true });
      }
    }

    if (form.valid) {
      console.log('Form value being sent:', form.value);
      this.modalService.close(modal.id, form.value);
    } else {
      console.log('Form is invalid:', form.errors);
      console.log('Form controls errors:', {
        currentPassword: form.get('currentPassword')?.errors,
        newPassword: form.get('newPassword')?.errors,
        confirmPassword: form.get('confirmPassword')?.errors
      });

      setTimeout(() => {
        const firstInvalidField = document.querySelector('.modal__form-field .ng-invalid:not(.modal__field--hidden)');
        if (firstInvalidField && firstInvalidField instanceof HTMLElement) {
          firstInvalidField.focus();
        }
      });

      this.cdr.markForCheck();
    }
  }

  shouldShowIcon(modal: ModalState): boolean {
    return !!modal.options.type && [
      ModalType.INFO,
      ModalType.WARNING,
      ModalType.ERROR,
      ModalType.CONFIRM
    ].includes(modal.options.type);
  }

  getPasswordCriteria(password: string) {
    return [
      {
        id: 'length',
        text: 'Minimum 8 characters',
        met: password.length >= 8
      },
      {
        id: 'case',
        text: 'At least one uppercase and one lowercase letter',
        met: /[a-z]/.test(password) && /[A-Z]/.test(password)
      },
      {
        id: 'number',
        text: 'At least one number',
        met: /\d/.test(password)
      },
      {
        id: 'special',
        text: 'At least one special character (@$!%*?&)',
        met: /[@$!%*?&]/.test(password)
      },
      {
        id: 'no-invalid-chars',
        text: 'No invalid characters (dots, spaces, etc.)',
        met: /^[A-Za-z\d@$!%*?&]*$/.test(password)
      }
    ];
  }

  getPasswordScore(password: string): number {
    return this.getPasswordCriteria(password).filter(c => c.met).length;
  }

  getPasswordStrengthColor(password: string): string {
    const score = this.getPasswordScore(password);
    return score <= 2 ? 'red' : score <= 3 ? 'orange' : 'green';
  }

  createPasswordValidator(): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.value;
      if (!password) return null;

      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

      if (!passwordRegex.test(password)) {
        return { passwordStrength: true };
      }

      return null;
    };
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('newPassword')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;

    if (!password || !confirmPassword) {
      return null;
    }

    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  togglePasswordVisibility(modalId: string, fieldName: string): void {
    if (!this.passwordVisibility.has(modalId)) {
      this.passwordVisibility.set(modalId, {});
    }

    const modalVisibility = this.passwordVisibility.get(modalId)!;
    modalVisibility[fieldName] = !modalVisibility[fieldName];
  }

  isPasswordVisible(modalId: string, fieldName: string): boolean {
    const modalVisibility = this.passwordVisibility.get(modalId);
    return modalVisibility?.[fieldName] || false;
  }
  resolveText(text: ModalText | undefined): string {
    if (typeof text === 'function') return text() ?? '';
    return text ?? '';
  }

  copyLink(link: string | undefined) {
    if (!link) return;
    navigator.clipboard.writeText(link).then(() => {
      this.toastService.success(this.translateData?.link_copied || 'Link copied to clipboard');
    });
  }

  shareTo(platform: string, link: string | undefined) {
    if (!link) return;

    let url = '';
    switch (platform) {
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(link)}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(link)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`;
        break;
      case 'telegram':
        url = `https://t.me/share/url?url=${encodeURIComponent(link)}`;
        break;
    }

    window.open(url, '_blank');
  }

  downloadImage(imageUrl: string, filename: string = 'image.png'): void {
    if (imageUrl.startsWith('data:')) {
      const arr = imageUrl.split(',');
      const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      const blob = new Blob([u8arr], { type: mime });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // ============ CREATE_VERSION / EDIT_VERSION ============
  private updateVersionData(modal: ModalState, partial: Partial<VersionFileModalData>): void {
    this.modalService.update(modal.id, { data: { ...modal.options.data, ...partial } });
    this.cdr.markForCheck();
  }

  private getVersionFileExtension(name: string): string {
    const match = name?.match(/\.([^.]+)$/);
    return match ? match[1].toLowerCase() : '';
  }

  isVersionFileSelectedPdf(modal: ModalState): boolean {
    const data = modal.options.data as VersionFileModalData;
    return this.getVersionFileExtension(data?.selectedFile?.name || '') === 'pdf';
  }

  /**
   * En edición, la sección para adjuntar archivo solo debe verse cuando el
   * usuario ha introducido una versión nueva (nombre distinto al original y
   * que no coincide con ninguna del historial); si no, se están editando los
   * metadatos de la versión actual y no hace falta adjuntar nada.
   */
  isEnteringNewVersion(modal: ModalState): boolean {
    const data = modal.options.data as VersionFileModalData;
    if (!data?.isEditMode) return true;
    const versionName = (data.selectedVersionItem ?? '').toString().trim();
    if (!versionName || versionName === (data.originalVersionName ?? '').toString().trim()) return false;
    return !findExistingVersion(data.versionHistory, versionName);
  }

  private applyVersionFileSelection(modal: ModalState, file: File): void {
    const data = modal.options.data as VersionFileModalData;
    let customFileName = data.customFileName;
    if (!customFileName) {
      customFileName = file.name.includes('.') ? file.name.split('.').slice(0, -1).join('.') : file.name;
    }

    const patch: Partial<VersionFileModalData> = { selectedFile: file, customFileName, fileDropActive: false };

    if (this.getVersionFileExtension(file.name) !== 'pdf' && data.showSignature) {
      patch.showSignature = false;
      patch.signatureSelectedUsers = [];
      patch.signatureOptions = [];
      patch.isLoadingSignatureOptions = false;
      this.clearSignatureSearchTimer();
    }

    this.updateVersionData(modal, patch);
  }

  onVersionFileDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onVersionFileDragEnter(event: DragEvent, modal: ModalState): void {
    event.preventDefault();
    this.updateVersionData(modal, { fileDropActive: true });
  }

  onVersionFileDragLeave(event: DragEvent, modal: ModalState): void {
    const target = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as Node | null;
    if (!relatedTarget || !target.contains(relatedTarget)) {
      this.updateVersionData(modal, { fileDropActive: false });
    }
  }

  onVersionFileDrop(event: DragEvent, modal: ModalState): void {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer?.files[0];
    if (!file) {
      this.updateVersionData(modal, { fileDropActive: false });
      return;
    }
    this.applyVersionFileSelection(modal, file);
  }

  onVersionFileSelected(event: Event, modal: ModalState): void {
    const input = event.target as HTMLInputElement;
    if (input?.files?.length) {
      this.applyVersionFileSelection(modal, input.files[0]);
    }
  }

  onVersionFieldChange(modal: ModalState, field: keyof VersionFileModalData, value: any): void {
    this.updateVersionData(modal, { [field]: value } as Partial<VersionFileModalData>);
  }

  onVersionShowListChange(modal: ModalState, enabled: boolean): void {
    const patch: Partial<VersionFileModalData> = { showList: enabled };
    if (!enabled) {
      patch.selectedVersionItem = null;
      patch.versionDate = '';
      patch.versionDateError = false;
    }
    this.updateVersionData(modal, patch);
  }

  enableVersionAndFocus(modal: ModalState): void {
    this.onVersionShowListChange(modal, true);
    // El campo está deshabilitado hasta que showList es true; esperamos al
    // siguiente ciclo para abrir el selector y poder escribir directamente.
    setTimeout(() => this.versionSelectRef?.open());
  }

  onVersionShowExpirationChange(modal: ModalState, enabled: boolean): void {
    const patch: Partial<VersionFileModalData> = { showExpiration: enabled };
    if (!enabled) patch.expirationDate = '';
    this.updateVersionData(modal, patch);
  }

  enableExpirationAndFocus(modal: ModalState): void {
    this.onVersionShowExpirationChange(modal, true);
    setTimeout(() => this.expirationInput?.nativeElement?.focus());
  }

  onVersionSearch(modal: ModalState, term: string): void {
    this.updateVersionData(modal, { versionSearchTerm: term });
  }

  onVersionChange(modal: ModalState): void {
    const data = modal.options.data as VersionFileModalData;
    const patch: Partial<VersionFileModalData> = { versionSearchTerm: '' };
    const matched = findExistingVersion(data.versionHistory, (data.selectedVersionItem ?? '').toString());
    if (matched?.itemVersionDate) {
      patch.versionDate = matched.itemVersionDate;
    }
    this.updateVersionData(modal, patch);
  }

  onCommitTypedVersion(modal: ModalState): void {
    const data = modal.options.data as VersionFileModalData;
    const term = (data.versionSearchTerm ?? '').trim();
    if (!term || term === data.selectedVersionItem) {
      if (data.versionSearchTerm) this.updateVersionData(modal, { versionSearchTerm: '' });
      return;
    }
    // El texto escrito se usa como versión seleccionada: si no coincide con una
    // versión existente se enviará como nueva versión.
    this.updateVersionData(modal, { selectedVersionItem: term, versionSearchTerm: '' });
  }

  onVersionDateFocus(modal: ModalState): void {
    this.updateVersionData(modal, { versionDate: '' });
  }

  onVersionDateInput(event: Event, modal: ModalState): void {
    const input = event.target as HTMLInputElement;
    const isDeletion = (event as InputEvent).inputType?.startsWith('delete') ?? false;
    const hasSlash = input.value.includes('/');
    const raw = input.value.replace(/[^\d]/g, '').substring(0, 6);
    const formatted = formatVersionDateInput(raw, isDeletion, hasSlash);
    input.value = formatted;
    this.updateVersionData(modal, { versionDate: formatted, versionDateError: false });
  }

  onVersionShowAcknowledgmentChange(modal: ModalState, enabled: boolean): void {
    const patch: Partial<VersionFileModalData> = { showAcknowledgment: enabled };
    if (!enabled) {
      patch.acknowledgmentSelectedUsers = [];
      patch.acknowledgmentOptions = [];
      patch.isLoadingAcknowledgmentOptions = false;
      this.clearAcknowledgmentSearchTimer();
    }
    this.updateVersionData(modal, patch);
  }

  onVersionShowSignatureChange(modal: ModalState, enabled: boolean): void {
    const patch: Partial<VersionFileModalData> = { showSignature: enabled };
    if (!enabled) {
      patch.signatureSelectedUsers = [];
      patch.signatureOptions = [];
      patch.isLoadingSignatureOptions = false;
      this.clearSignatureSearchTimer();
    }
    this.updateVersionData(modal, patch);
  }

  enableVersionSignatureIfAllowed(modal: ModalState): void {
    if (!this.isVersionFileSelectedPdf(modal)) return;
    const data = modal.options.data as VersionFileModalData;
    if (!data.showSignature) {
      this.onVersionShowSignatureChange(modal, true);
    }
  }

  private clearAcknowledgmentSearchTimer(): void {
    if (this.acknowledgmentSearchTimer) {
      clearTimeout(this.acknowledgmentSearchTimer);
      this.acknowledgmentSearchTimer = null;
    }
  }

  private clearSignatureSearchTimer(): void {
    if (this.signatureSearchTimer) {
      clearTimeout(this.signatureSearchTimer);
      this.signatureSearchTimer = null;
    }
  }

  onVersionAcknowledgmentSearch(modal: ModalState, term: string): void {
    const data = modal.options.data as VersionFileModalData;
    if (!term || term.length < 2 || !data.onAcknowledgmentSearch) {
      this.updateVersionData(modal, { acknowledgmentOptions: [], isLoadingAcknowledgmentOptions: false });
      return;
    }
    this.updateVersionData(modal, { isLoadingAcknowledgmentOptions: true });
    this.clearAcknowledgmentSearchTimer();
    this.acknowledgmentSearchTimer = setTimeout(() => {
      data.onAcknowledgmentSearch!(term).subscribe({
        next: (options) => this.updateVersionData(modal, { acknowledgmentOptions: options, isLoadingAcknowledgmentOptions: false }),
        error: () => this.updateVersionData(modal, { acknowledgmentOptions: [], isLoadingAcknowledgmentOptions: false })
      });
    }, 300);
  }

  onVersionSignatureSearch(modal: ModalState, term: string): void {
    const data = modal.options.data as VersionFileModalData;
    if (!term || term.length < 2 || !data.onSignatureSearch) {
      this.updateVersionData(modal, { signatureOptions: [], isLoadingSignatureOptions: false });
      return;
    }
    this.updateVersionData(modal, { isLoadingSignatureOptions: true });
    this.clearSignatureSearchTimer();
    this.signatureSearchTimer = setTimeout(() => {
      data.onSignatureSearch!(term).subscribe({
        next: (options) => this.updateVersionData(modal, { signatureOptions: options, isLoadingSignatureOptions: false }),
        error: () => this.updateVersionData(modal, { signatureOptions: [], isLoadingSignatureOptions: false })
      });
    }, 300);
  }

  isModalButtonDisabled(modal: ModalState, button: ModalButton): boolean {
    if (modal.options.type === ModalType.CREATE_VERSION || modal.options.type === ModalType.EDIT_VERSION) {
      const data = modal.options.data as VersionFileModalData;
      if (button.value === 'delete') return !!data?.isSubmittingEdit;
      // El dropzone puede mostrar el nombre del archivo actual antes de que
      // selectedFile esté realmente listo (se está precargando en segundo
      // plano): no dejar guardar hasta que termine, para no dar "no se ha
      // seleccionado archivo" con uno aparentemente ya puesto.
      if (button.value === 'submit') return !!data?.isPreloadingFile;
    }
    return false;
  }

  private setVersionError(modal: ModalState, message: string, versionDateError = false): void {
    const patch: Partial<VersionFileModalData> = { errorMessage: message };
    if (versionDateError) patch.versionDateError = true;
    this.updateVersionData(modal, patch);
    this.toastService.error(this.translateData?.error || 'Error', message);
  }

  submitVersionFile(modal: ModalState): void {
    const data = modal.options.data as VersionFileModalData;
    const messages = data.messages || {};
    const versionName = (data.selectedVersionItem ?? '').toString().trim();

    if (data.showExpiration && !data.expirationDate) {
      this.setVersionError(modal, messages.expirationDateRequired || 'You must select the expiration date');
      return;
    }

    if (data.showExpiration && !isValidDateString(data.expirationDate)) {
      this.setVersionError(modal, messages.expirationDateInvalid || 'The expiration date is not valid');
      return;
    }

    if (data.showExpiration && data.expirationDate < data.todayDateString) {
      this.setVersionError(modal, messages.expirationDatePast || 'The expiration date cannot be before today');
      return;
    }

    const versionNameCandidate = (data.selectedVersionItem ?? '').toString().trim();
    if (data.showList && data.requireVersionName && !versionNameCandidate) {
      this.setVersionError(modal, messages.versionNameRequired || 'Version name is required');
      return;
    }

    if (data.showList && !data.versionDate) {
      this.setVersionError(modal, messages.versionDateRequired || 'Version date is required (format MM/YYYY)', true);
      return;
    }

    if (data.showList && data.versionDate && !isValidMonthString(data.versionDate)) {
      this.setVersionError(modal, messages.versionDateInvalid || 'The version date is not valid', true);
      return;
    }

    if (data.showList && data.versionDate && isVersionDateInFuture(data.versionDate)) {
      this.setVersionError(modal, messages.versionDateFuture || 'The version date cannot be in the future', true);
      return;
    }

    if (data.showExpiration && data.versionDate && isExpirationBeforeVersionMonth(data.expirationDate, data.versionDate)) {
      this.setVersionError(modal, messages.expirationAfterVersion || 'The expiration date must be equal to or after the version date');
      return;
    }

    let matchedVersionId: string | null = null;

    // Prioridad 1: si estamos editando y NI el nombre de versión NI el
    // archivo cambiaron respecto a lo que había al abrir el modal, el
    // usuario no pidió crear una versión nueva: se editan los metadatos de
    // currentVersionId (el id real de la versión abierta, garantizado
    // correcto) SIN depender del matching por nombre contra el historial,
    // que puede traer un id representado de forma distinta (y por tanto
    // apuntar la edición al sitio equivocado) o fallar si la versión actual
    // no tiene nombre. Tampoco hace falta adjuntar archivo en este caso.
    if (data.isEditMode && data.currentVersionId) {
      const nameUnchanged = versionName === (data.originalVersionName ?? '').trim();
      const fileUnchanged = !data.selectedFile || data.selectedFile === data.originalSelectedFile;
      if (nameUnchanged && fileUnchanged) {
        matchedVersionId = data.currentVersionId;
      }
    }

    // Prioridad 2: el nombre cambió y coincide con OTRA versión ya existente
    // del historial: el usuario pidió explícitamente editar esa versión.
    if (!matchedVersionId) {
      const matchedVersion = findExistingVersion(data.versionHistory, versionName);
      if (matchedVersion) {
        const matchedId = String(matchedVersion.id ?? matchedVersion._id ?? '');
        if (matchedId) {
          if (!(data.customFileName || '').trim()) {
            this.setVersionError(modal, messages.enterName || 'Enter a name');
            return;
          }
          matchedVersionId = matchedId;
        }
      }
    }

    if (!matchedVersionId) {
      // En edición, el archivo precargado de la versión actual no cuenta como
      // "adjuntado": una versión nueva de verdad necesita su propio archivo.
      if (!data.selectedFile || (data.isEditMode && data.selectedFile === data.originalSelectedFile)) {
        this.setVersionError(modal, messages.noFileSelected || 'No file selected');
        return;
      }
      const trimmedName = (data.customFileName || '').trim();
      if (trimmedName && !isValidItemName(trimmedName)) {
        this.setVersionError(
          modal,
          messages.invalidName ||
            'El nombre solo puede contener letras, números, espacios, guiones, guiones bajos y puntos'
        );
        return;
      }
    }

    const result: VersionFileSubmitResult = {
      customFileName: (data.customFileName || '').trim(),
      selectedFile: data.selectedFile,
      versionName,
      versionDate: data.versionDate,
      showExpiration: data.showExpiration,
      expirationDate: data.expirationDate,
      fileNotes: data.fileNotes,
      showAcknowledgment: data.showAcknowledgment,
      acknowledgmentUserIds: (data.acknowledgmentSelectedUsers || []).map((u) => u.id),
      showSignature: data.showSignature,
      signatureUserIds: (data.signatureSelectedUsers || []).map((u) => u.id),
      matchedVersionId
    };

    this.modalService.close(modal.id, result);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeyDown(event: Event): void {
    const topmostModal = this.modals
      .filter(modal => modal.visible)
      .sort((a, b) => (b.options.zIndex || 1000) - (a.options.zIndex || 1000))[0];

    if (topmostModal && topmostModal.options.closable) {
      this.closeModal(topmostModal);
      event.preventDefault();
      event.stopPropagation();
    }
  }

}